'use client'

import { Card, Cell, Image, Text } from '@vinted/web-ui'
import { useContext, ReactNode } from 'react'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { useAsset } from '@marketplace-web/shared/assets'
import { ShippingDetailsContext } from '@marketplace-web/item-page/item-page-data'
import { EscrowFeesContext } from '@marketplace-web/escrow-pricing/escrow-pricing-data'

import { ItemPageBuyerProtectionInfoPluginModel } from './types'
import { BUYER_PROTECTION_PRO_URL } from '../../constants/routes'

type Props = {
  data: ItemPageBuyerProtectionInfoPluginModel
}

const ItemPageBuyerProtectionInfoPlugin = ({ data }: Props) => {
  const { user } = useSession()
  const translate = useTranslate('item.service_fee')
  const asset = useAsset('/assets/buyer-protection')
  const { shippingDetails } = useContext(ShippingDetailsContext)
  const { escrowFees } = useContext(EscrowFeesContext)

  const { isSellerBusiness, itemUserId } = data
  const isViewingSelf = user?.id === itemUserId

  if (!shippingDetails || isViewingSelf || !escrowFees?.buyerProtection?.finalPrice) return null

  const title = isSellerBusiness ? translate('business.title') : translate('title')
  const proTermsAndConditionsLinkValue = {
    'buyer-protection-pro-link': (chunks: Array<ReactNode>) => (
      <a href={BUYER_PROTECTION_PRO_URL} target="_blank" rel="noopener noreferrer">
        {chunks}
      </a>
    ),
  }
  const description = isSellerBusiness
    ? translate('business.description_v2', proTermsAndConditionsLinkValue)
    : translate('description')
  const getImageName = isSellerBusiness
    ? 'buyer-protection-pro-shield.svg'
    : 'buyer-protection-shield.svg'
  const shieldImagePath = asset(getImageName, { theme: { dark: `dark/${getImageName}` } })

  const renderContent = () => (
    <Cell
      styling="tight"
      prefix={<Image src={shieldImagePath} size="medium" testId="item-service-fee-shield-image" />}
      title={
        <Text as="h2" text={title} theme="amplified" type="title" testId="item-service-fee-title" />
      }
      body={
        <Text
          as="h3"
          text={description}
          theme="inherit"
          type="subtitle"
          testId={isSellerBusiness ? 'item-safety-description-business' : 'item-safety-description'}
          html
        />
      }
    />
  )

  return (
    <Card>
      <div className="u-overflow-hidden">
        <Cell>{renderContent()}</Cell>
      </div>
    </Card>
  )
}

export default ItemPageBuyerProtectionInfoPlugin
